import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCw, Search } from "lucide-react";
import { useMemo, useState, useCallback } from "react";
import { SearchableMultiFilterSelect } from "@sd/components/common/SearchableMultiFilterSelect";
import { CreatedDateFilterSelect } from "@sd/components/common/CreatedDateFilterSelect";
import { TICKET_STATUS_DROPDOWN_OPTIONS, sanitizeStatusFilterValues } from "@sd/constants/ticketStatuses";
import { TICKET_TYPE_FILTER_OPTIONS } from "@sd/types/ticket";
import type { CreatedDateFilterState } from "@sd/lib/ticketCreatedDateFilter";
import { hasActiveTicketFilters, DEFAULT_MODULE_FILTER } from "@sd/lib/ticketFilterDefaults";
import {
  buildCategoryFilterSelectOptions,
  normalizeCategoryFilterValues,
} from "@sd/lib/ticketCategoryOptions";
import {
  DEFAULT_CREATE_UPDATE_FILTER,
  type CreateUpdateFilter,
} from "@sd/lib/ticketListFilterStorage";

interface TicketFilterBarProps {
  search: string;
  onSearchChange: (v: string) => void;
  statusFilters: string[];
  onStatusFiltersChange: (v: string[]) => void;
  priorityFilters: string[];
  onPriorityFiltersChange: (v: string[]) => void;
  priorityOptions?: { id: string; label: string }[];
  categoryFilters: string[];
  onCategoryFiltersChange: (v: string[]) => void;
  categories: string[];
  typeFilter?: string;
  onTypeFilterChange?: (v: string) => void;
  projectFilters?: string[];
  onProjectFiltersChange?: (v: string[]) => void;
  projectOptions?: { value: string; label: string }[];
  assigneeFilters?: string[];
  onAssigneeFiltersChange?: (v: string[]) => void;
  assigneeOptions?: { id: string; name: string }[];
  unassignedFilterValue?: string;
  requesterFilters?: string[];
  onRequesterFiltersChange?: (v: string[]) => void;
  requesterOptions?: { id: string; name: string }[];
  createdDateFilter?: CreatedDateFilterState;
  onCreatedDateFilterChange?: (v: CreatedDateFilterState) => void;
  /** VIP filter: "all" | "vip". When "vip", the list payload sends `is_vip: true`. */
  vipFilter?: string;
  onVipFilterChange?: (v: string) => void;
  /** SLA filter: "all" | "sla". When "sla", the list payload sends `is_sla: true`. */
  slaFilter?: string;
  onSlaFilterChange?: (v: string) => void;
  /**
   * Admin date-axis filter: "all" | "on_create" | "on_update".
   * Sends `on_create=true` or `on_update=true` when selected (default all).
   */
  createUpdateFilter?: CreateUpdateFilter;
  onCreateUpdateFilterChange?: (v: CreateUpdateFilter) => void;
  /** PTS module filter — only when a single project is selected. */
  moduleFilter?: string;
  onModuleFilterChange?: (v: string) => void;
  moduleOptions?: { value: string; label: string }[];
  showModuleFilter?: boolean;
  moduleOptionsLoading?: boolean;
  /** Optional element rendered after the selects (e.g. badge / page counter). */
  trailing?: React.ReactNode;
  /** Split layout only — actions on the second row (e.g. Add Ticket). */
  secondaryTrailing?: React.ReactNode;
  className?: string;
  /** Agent workbench only — mobile layout tweaks; desktop unchanged. */
  agentResponsive?: boolean;
  /** Admin / Super Agent: two balanced rows instead of one crowded line. */
  filterLayout?: "wrap" | "split";
  /** Clears all toolbar filters (search, dropdowns, dates). */
  onReset?: () => void;
  /** Admin Ticket Management — single dropdown for VIP / SLA instead of two. */
  mergeVipSlaFilter?: boolean;
}

/**
 * Reusable filter bar: Search + Status + Priority + Category.
 * Used in both the agent workbench toolbar and the requester grid header.
 */
export function TicketFilterBar({
  search,
  onSearchChange,
  statusFilters,
  onStatusFiltersChange,
  priorityFilters,
  onPriorityFiltersChange,
  priorityOptions = [],
  categoryFilters,
  onCategoryFiltersChange,
  categories,
  typeFilter,
  onTypeFilterChange,
  projectFilters,
  onProjectFiltersChange,
  projectOptions = [],
  assigneeFilters,
  onAssigneeFiltersChange,
  assigneeOptions = [],
  unassignedFilterValue = "__unassigned__",
  requesterFilters,
  onRequesterFiltersChange,
  requesterOptions = [],
  createdDateFilter,
  onCreatedDateFilterChange,
  vipFilter,
  onVipFilterChange,
  slaFilter,
  onSlaFilterChange,
  createUpdateFilter,
  onCreateUpdateFilterChange,
  moduleFilter,
  onModuleFilterChange,
  moduleOptions = [],
  showModuleFilter = false,
  moduleOptionsLoading = false,
  trailing,
  secondaryTrailing,
  className,
  agentResponsive = false,
  filterLayout = "wrap",
  onReset,
  mergeVipSlaFilter = false,
}: TicketFilterBarProps) {
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);

  const showTypeFilter =
    typeFilter !== undefined && onTypeFilterChange !== undefined;
  const showProjectFilter =
    projectFilters !== undefined && onProjectFiltersChange !== undefined;
  const showAssigneeFilter =
    assigneeFilters !== undefined && onAssigneeFiltersChange !== undefined;
  const showRequesterFilter =
    requesterFilters !== undefined && onRequesterFiltersChange !== undefined;
  const showCreatedDateFilter =
    createdDateFilter !== undefined && onCreatedDateFilterChange !== undefined;
  const showVipFilter =
    vipFilter !== undefined && onVipFilterChange !== undefined;
  const showSlaFilter =
    slaFilter !== undefined && onSlaFilterChange !== undefined;
  const showMergedVipSlaFilter =
    mergeVipSlaFilter && showVipFilter && showSlaFilter;
  const showCreateUpdateFilter =
    createUpdateFilter !== undefined &&
    onCreateUpdateFilterChange !== undefined;
  const showModuleFilterControl =
    showModuleFilter &&
    moduleFilter !== undefined &&
    onModuleFilterChange !== undefined;

  const priorityFilterSelectOptions = useMemo(
    () =>
      priorityOptions.map(opt => ({
        value: opt.id,
        label: opt.label,
      })),
    [priorityOptions]
  );

  const categoryFilterSelectOptions = useMemo(
    () => buildCategoryFilterSelectOptions(categories),
    [categories]
  );

  const handleCategoryFiltersChange = useCallback(
    (next: string[]) => onCategoryFiltersChange(normalizeCategoryFilterValues(next)),
    [onCategoryFiltersChange]
  );

  const projectFilterSelectOptions = useMemo(
    () => (showProjectFilter ? projectOptions : []),
    [showProjectFilter, projectOptions]
  );

  const assigneeFilterSelectOptions = useMemo(
    () =>
      showAssigneeFilter
        ? [
            { value: unassignedFilterValue, label: "Unassigned" },
            ...assigneeOptions.map(agent => ({
              value: agent.id,
              label: agent.name,
            })),
          ]
        : [],
    [showAssigneeFilter, unassignedFilterValue, assigneeOptions]
  );

  const requesterFilterSelectOptions = useMemo(
    () =>
      showRequesterFilter
        ? requesterOptions.map(r => ({
            value: r.id,
            label: r.name,
          }))
        : [],
    [showRequesterFilter, requesterOptions]
  );

  const statusFilterSelectOptions = useMemo(
    () =>
      TICKET_STATUS_DROPDOWN_OPTIONS.map(opt => ({
        value: opt.value,
        label: opt.label,
      })),
    []
  );

  const handleStatusFiltersChange = useCallback(
    (values: string[]) => {
      onStatusFiltersChange(sanitizeStatusFilterValues(values));
    },
    [onStatusFiltersChange]
  );

  const isSplit = filterLayout === "split";

  const hasActiveFilters = useMemo(
    () =>
      hasActiveTicketFilters({
        search,
        statusFilters,
        priorityFilters,
        categoryFilters,
        typeFilter: showTypeFilter ? typeFilter : undefined,
        projectFilters: showProjectFilter ? projectFilters : undefined,
        assigneeFilters: showAssigneeFilter ? assigneeFilters : undefined,
        requesterFilters: showRequesterFilter ? requesterFilters : undefined,
        createdDateFilter: showCreatedDateFilter ? createdDateFilter : undefined,
        moduleFilter: showModuleFilterControl ? moduleFilter : undefined,
      }) ||
      (showMergedVipSlaFilter
        ? vipFilter !== "all" || slaFilter !== "all"
        : (showVipFilter && vipFilter !== "all") ||
          (showSlaFilter && slaFilter !== "all")) ||
      (showCreateUpdateFilter &&
        createUpdateFilter !== DEFAULT_CREATE_UPDATE_FILTER),
    [
      search,
      statusFilters,
      priorityFilters,
      categoryFilters,
      showTypeFilter,
      typeFilter,
      showProjectFilter,
      projectFilters,
      showAssigneeFilter,
      assigneeFilters,
      showRequesterFilter,
      requesterFilters,
      showCreatedDateFilter,
      createdDateFilter,
      showMergedVipSlaFilter,
      showVipFilter,
      vipFilter,
      showSlaFilter,
      slaFilter,
      showCreateUpdateFilter,
      createUpdateFilter,
    ]
  );

  const handleReset = () => {
    setOpenFilterId(null);
    onReset?.();
  };

  const resetButton = onReset ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-9 shrink-0 gap-1.5"
      onClick={handleReset}
      disabled={!hasActiveFilters}
      title="Clear all filters"
      aria-label="Clear all filters"
    >
      <RefreshCw className="h-3.5 w-3.5" />
      Clear All Filters
    </Button>
  ) : null;

  const bindSelect = (id: string) => ({
    open: openFilterId === id,
    onOpenChange: (open: boolean) => setOpenFilterId(open ? id : null),
  });

  const searchField = (
    <div
      className={
        isSplit
          ? "relative min-w-[220px] flex-1 shrink-0"
          : agentResponsive
            ? "relative w-full min-w-0 max-w-xl flex-1 shrink-0 lg:min-w-[200px]"
            : "relative min-w-[200px] max-w-xl flex-1 shrink-0"
      }
    >
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        placeholder="Search ID, subject, requester, tags..."
        value={search}
        onChange={e => onSearchChange(e.target.value)}
        className={
          agentResponsive && !isSplit
            ? "h-9 w-full min-w-0 pl-9 lg:min-w-[180px]"
            : "h-9 w-full min-w-[180px] pl-9"
        }
      />
    </div>
  );

  const statusSelect = (
    <SearchableMultiFilterSelect
      selectId="status"
      openSelectId={openFilterId}
      onOpenSelectIdChange={setOpenFilterId}
      values={statusFilters}
      onValuesChange={handleStatusFiltersChange}
      options={statusFilterSelectOptions}
      placeholder="All Status"
      searchPlaceholder="Search status…"
      triggerClassName={
        isSplit
          ? "w-[160px] shrink-0"
          : agentResponsive
            ? "w-full shrink-0 lg:w-[180px]"
            : "w-[180px] shrink-0"
      }
    />
  );

  const prioritySelect = (
    <SearchableMultiFilterSelect
      selectId="priority"
      openSelectId={openFilterId}
      onOpenSelectIdChange={setOpenFilterId}
      values={priorityFilters}
      onValuesChange={onPriorityFiltersChange}
      options={priorityFilterSelectOptions}
      placeholder="All Priority"
      searchPlaceholder="Search priority…"
      triggerClassName={
        isSplit
          ? "w-[140px] shrink-0"
          : agentResponsive
            ? "w-full shrink-0 lg:w-[140px]"
            : "w-[140px] shrink-0"
      }
    />
  );

  const categorySelect = (
    <SearchableMultiFilterSelect
      selectId="category"
      openSelectId={openFilterId}
      onOpenSelectIdChange={setOpenFilterId}
      values={categoryFilters}
      onValuesChange={handleCategoryFiltersChange}
      options={categoryFilterSelectOptions}
      placeholder="All Categories"
      searchPlaceholder="Search category…"
      triggerClassName={
        isSplit
          ? "w-[150px] shrink-0"
          : agentResponsive
            ? "w-full shrink-0 lg:w-[150px]"
            : "w-[150px] shrink-0"
      }
    />
  );

  const typeSelect = showTypeFilter ? (
    <Select
      value={typeFilter}
      onValueChange={v => {
        onTypeFilterChange(v);
        setOpenFilterId(null);
      }}
      {...bindSelect("type")}
    >
      <SelectTrigger
        className={
          isSplit
            ? "h-9 w-[140px] shrink-0"
            : agentResponsive
              ? "h-9 w-full shrink-0 lg:w-[140px]"
              : "h-9 w-[140px] shrink-0"
        }
      >
        <SelectValue placeholder="Ticket Type" />
      </SelectTrigger>
      <SelectContent side="bottom" align="start" avoidCollisions={false}>
        <SelectItem value="all">All Ticket Types</SelectItem>
        {TICKET_TYPE_FILTER_OPTIONS.map(opt => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : null;

  const projectSelect = showProjectFilter ? (
    <SearchableMultiFilterSelect
      selectId="project"
      openSelectId={openFilterId}
      onOpenSelectIdChange={setOpenFilterId}
      values={projectFilters}
      onValuesChange={onProjectFiltersChange}
      options={projectFilterSelectOptions}
      placeholder="All Projects"
      searchPlaceholder="Search projects…"
      triggerClassName={
        isSplit
          ? "w-[150px] shrink-0"
          : agentResponsive
            ? "w-full shrink-0 lg:w-[150px]"
            : "w-[150px] shrink-0"
      }
    />
  ) : null;

  const assigneeSelect = showAssigneeFilter ? (
    <SearchableMultiFilterSelect
      selectId="assignee"
      openSelectId={openFilterId}
      onOpenSelectIdChange={setOpenFilterId}
      values={assigneeFilters}
      onValuesChange={onAssigneeFiltersChange}
      options={assigneeFilterSelectOptions}
      placeholder="All Assignees"
      searchPlaceholder="Search assignees…"
      triggerClassName={
        isSplit
          ? "w-[150px] shrink-0"
          : agentResponsive
            ? "w-full shrink-0 lg:w-[150px]"
            : "w-[150px] shrink-0"
      }
    />
  ) : null;

  const requesterSelect = showRequesterFilter ? (
    <SearchableMultiFilterSelect
      selectId="requester"
      openSelectId={openFilterId}
      onOpenSelectIdChange={setOpenFilterId}
      values={requesterFilters}
      onValuesChange={onRequesterFiltersChange}
      options={requesterFilterSelectOptions}
      placeholder="All Requesters"
      searchPlaceholder="Search requesters…"
      triggerClassName={
        isSplit
          ? "w-[160px] shrink-0"
          : agentResponsive
            ? "w-full shrink-0 lg:w-[160px]"
            : "w-[160px] shrink-0"
      }
    />
  ) : null;

  const dateRange = showCreatedDateFilter ? (
    <CreatedDateFilterSelect
      value={createdDateFilter}
      onChange={onCreatedDateFilterChange}
      agentResponsive={agentResponsive && !isSplit}
    />
  ) : null;

  const moduleSelect = showModuleFilterControl ? (
    <SearchableMultiFilterSelect
      selectId="module"
      openSelectId={openFilterId}
      onOpenSelectIdChange={setOpenFilterId}
      values={
        moduleFilter && moduleFilter !== DEFAULT_MODULE_FILTER
          ? [moduleFilter]
          : []
      }
      onValuesChange={values => {
        onModuleFilterChange(values[values.length - 1] ?? DEFAULT_MODULE_FILTER);
      }}
      options={moduleOptions}
      placeholder={moduleOptionsLoading ? "Loading modules…" : "All Modules"}
      searchPlaceholder="Search module…"
      disabled={moduleOptionsLoading}
      triggerClassName={
        isSplit
          ? "w-[170px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
          : agentResponsive
            ? "w-full shrink-0 lg:w-[170px]"
            : "w-[170px] shrink-0"
      }
    />
  ) : null;

  const vipSelect = showVipFilter && !showMergedVipSlaFilter ? (
    <Select
      value={vipFilter}
      onValueChange={v => {
        onVipFilterChange(v);
        setOpenFilterId(null);
      }}
      {...bindSelect("vip")}
    >
      <SelectTrigger
        className={
          isSplit
            ? "h-9 w-[140px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
            : agentResponsive
              ? "h-9 w-full shrink-0 lg:w-[140px]"
              : "h-9 w-[140px] shrink-0"
        }
      >
        <SelectValue placeholder="VIP" />
      </SelectTrigger>
      <SelectContent side="bottom" align="start" avoidCollisions={false}>
        <SelectItem value="all">All Tickets</SelectItem>
        <SelectItem value="vip">VIP Only</SelectItem>
      </SelectContent>
    </Select>
  ) : null;

  const slaSelect = showSlaFilter && !showMergedVipSlaFilter ? (
    <Select
      value={slaFilter}
      onValueChange={v => {
        onSlaFilterChange(v);
        setOpenFilterId(null);
      }}
      {...bindSelect("sla")}
    >
      <SelectTrigger
        className={
          isSplit
            ? "h-9 w-[140px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
            : agentResponsive
              ? "h-9 w-full shrink-0 lg:w-[140px]"
              : "h-9 w-[140px] shrink-0"
        }
      >
        <SelectValue placeholder="SLA" />
      </SelectTrigger>
      <SelectContent side="bottom" align="start" avoidCollisions={false}>
        <SelectItem value="all">All Tickets</SelectItem>
        <SelectItem value="sla">SLA On</SelectItem>
      </SelectContent>
    </Select>
  ) : null;

  const mergedVipSlaValue =
    vipFilter === "vip" && slaFilter === "all"
      ? "vip"
      : slaFilter === "sla" && vipFilter === "all"
        ? "sla"
        : "all";

  const mergedVipSlaSelect = showMergedVipSlaFilter ? (
    <Select
      value={mergedVipSlaValue}
      onValueChange={v => {
        if (v === "vip") {
          onVipFilterChange!("vip");
          onSlaFilterChange!("all");
        } else if (v === "sla") {
          onVipFilterChange!("all");
          onSlaFilterChange!("sla");
        } else {
          onVipFilterChange!("all");
          onSlaFilterChange!("all");
        }
        setOpenFilterId(null);
      }}
      {...bindSelect("vipSla")}
    >
      <SelectTrigger
        className={
          isSplit
            ? "h-9 w-[140px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
            : agentResponsive
              ? "h-9 w-full shrink-0 lg:w-[140px]"
              : "h-9 w-[140px] shrink-0"
        }
      >
        <SelectValue placeholder="All Tickets" />
      </SelectTrigger>
      <SelectContent side="bottom" align="start" avoidCollisions={false}>
        <SelectItem value="all">All Tickets</SelectItem>
        <SelectItem value="vip">VIP Only</SelectItem>
        <SelectItem value="sla">SLA On</SelectItem>
      </SelectContent>
    </Select>
  ) : null;

  const createUpdateSelect = showCreateUpdateFilter ? (
    <Select
      value={createUpdateFilter}
      onValueChange={v => {
        const next: CreateUpdateFilter =
          v === "on_update" || v === "on_create" || v === "all"
            ? v
            : DEFAULT_CREATE_UPDATE_FILTER;
        onCreateUpdateFilterChange(next);
        setOpenFilterId(null);
      }}
      {...bindSelect("createUpdate")}
    >
      <SelectTrigger
        className={
          isSplit
            ? "h-9 w-[150px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
            : agentResponsive
              ? "h-9 w-full shrink-0 lg:w-[150px]"
              : "h-9 w-[150px] shrink-0"
        }
      >
        <SelectValue placeholder="All Tickets" />
      </SelectTrigger>
      <SelectContent side="bottom" align="start" avoidCollisions={false}>
        <SelectItem value="all">All Tickets</SelectItem>
        <SelectItem value="on_create">On Create</SelectItem>
        <SelectItem value="on_update">On Update</SelectItem>
      </SelectContent>
    </Select>
  ) : null;

  if (isSplit) {
    return (
      <div className={`flex flex-col gap-2.5 ${className ?? ""}`}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1 basis-[200px] shrink-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search ID, subject, requester, tags..."
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              className="h-9 w-full min-w-0 pl-9"
            />
          </div>
          <SearchableMultiFilterSelect
            selectId="status"
            openSelectId={openFilterId}
            onOpenSelectIdChange={setOpenFilterId}
            values={statusFilters}
            onValuesChange={handleStatusFiltersChange}
            options={statusFilterSelectOptions}
            placeholder="All Status"
            searchPlaceholder="Search status…"
            triggerClassName="w-[160px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
          />
          <SearchableMultiFilterSelect
            selectId="priority"
            openSelectId={openFilterId}
            onOpenSelectIdChange={setOpenFilterId}
            values={priorityFilters}
            onValuesChange={onPriorityFiltersChange}
            options={priorityFilterSelectOptions}
            placeholder="All Priority"
            searchPlaceholder="Search priority…"
            triggerClassName="w-[140px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
          />
          <SearchableMultiFilterSelect
            selectId="category"
            openSelectId={openFilterId}
            onOpenSelectIdChange={setOpenFilterId}
            values={categoryFilters}
            onValuesChange={handleCategoryFiltersChange}
            options={categoryFilterSelectOptions}
            placeholder="All Categories"
            searchPlaceholder="Search category…"
            triggerClassName="w-[150px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
          />
          {showTypeFilter ? (
            <Select
              value={typeFilter}
              onValueChange={v => {
                onTypeFilterChange(v);
                setOpenFilterId(null);
              }}
              {...bindSelect("type")}
            >
              <SelectTrigger className="h-9 w-[140px] max-xl:min-w-[110px] max-xl:flex-1 shrink-0">
                <SelectValue placeholder="Ticket Type" />
              </SelectTrigger>
              <SelectContent side="bottom" align="start" avoidCollisions={false}>
                <SelectItem value="all">All Ticket Types</SelectItem>
                {TICKET_TYPE_FILTER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {trailing ? (
            <div className="ml-auto w-full shrink-0 max-xl:ml-0 max-xl:flex max-xl:justify-end xl:w-auto">
              {trailing}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showProjectFilter ? (
            <SearchableMultiFilterSelect
              selectId="project"
              openSelectId={openFilterId}
              onOpenSelectIdChange={setOpenFilterId}
              values={projectFilters}
              onValuesChange={onProjectFiltersChange}
              options={projectFilterSelectOptions}
              placeholder="All Projects"
              searchPlaceholder="Search projects…"
              triggerClassName="w-[150px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
            />
          ) : null}
          {moduleSelect}
          {showAssigneeFilter ? (
            <SearchableMultiFilterSelect
              selectId="assignee"
              openSelectId={openFilterId}
              onOpenSelectIdChange={setOpenFilterId}
              values={assigneeFilters}
              onValuesChange={onAssigneeFiltersChange}
              options={assigneeFilterSelectOptions}
              placeholder="All Assignees"
              searchPlaceholder="Search assignees…"
              triggerClassName="w-[150px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
            />
          ) : null}
          {showRequesterFilter ? (
            <SearchableMultiFilterSelect
              selectId="requester"
              openSelectId={openFilterId}
              onOpenSelectIdChange={setOpenFilterId}
              values={requesterFilters}
              onValuesChange={onRequesterFiltersChange}
              options={requesterFilterSelectOptions}
              placeholder="All Requesters"
              searchPlaceholder="Search requesters…"
              triggerClassName="w-[160px] max-xl:min-w-[120px] max-xl:flex-1 shrink-0"
            />
          ) : null}
          {showCreatedDateFilter ? (
            <CreatedDateFilterSelect
              value={createdDateFilter}
              onChange={onCreatedDateFilterChange}
            />
          ) : null}
          {mergedVipSlaSelect ?? vipSelect}
          {slaSelect}
          {createUpdateSelect}
          {resetButton}
          {secondaryTrailing ? (
            <div className="ml-auto flex w-full shrink-0 flex-wrap items-center justify-end gap-2 max-xl:ml-0 xl:w-auto">
              {secondaryTrailing}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className ?? ""}`}>
      {searchField}
      {statusSelect}
      {prioritySelect}
      {categorySelect}
      {typeSelect}
      {projectSelect}
      {moduleSelect}
      {assigneeSelect}
      {requesterSelect}
      {dateRange}
      {mergedVipSlaSelect ?? vipSelect}
      {slaSelect}
      {createUpdateSelect}
      {resetButton}
      {trailing}
    </div>
  );
}
